import {inject, Injector, Injectable} from '@angular/core';
import {firstValueFrom} from 'rxjs';
import {ConversationApiService} from '@shared/services/api/conversation/conversation-api.service';
import {ConversationListService} from './conversation-list.service';
import {IConversation} from '../models/conversation.model';
import {CHANNEL} from '../models/channel.model';

/**
 * Service đồng bộ thông tin khách hàng (tên + avatar) từ nền tảng.
 *
 * Flow:
 * 1. Phát hiện tên mặc định ("Facebook User", "Zalo User"...)
 *    hoặc avatar lỗi (403/4xx) → gọi API updateCustomer(syncName: 1)
 * 2. BE fetch tên/avatar thật từ nền tảng (FB, Zalo...)
 * 3. Cập nhật vào conversation list qua ConversationListService
 * 4. List re-render → UI hiển thị tên/avatar thật
 *
 * Chỉ hỗ trợ kênh Facebook và Zalo cá nhân (BE side).
 * Gọi API lần lượt bằng await để tránh áp lực BE.
 */
@Injectable({providedIn: 'root'})
export class CustomerSyncService {
  private readonly api = inject(ConversationApiService);
  private readonly injector = inject(Injector);
  private _convListSvc: ConversationListService | null = null;

  /** Lazy get ConversationListService — tránh circular dependency NG0200 */
  private get convListSvc(): ConversationListService {
    if (!this._convListSvc) {
      this._convListSvc = this.injector.get(ConversationListService);
    }
    return this._convListSvc;
  }

  /** Track các conversation đang sync — tránh gọi trùng lặp */
  private _syncing = new Set<string>();

  /** Tên mặc định từ BE khi API nền tảng không lấy được tên thật */
  static readonly DEFAULT_CUSTOMER_NAMES = new Set([
    'Facebook User',
    'Instagram User',
    'Shopee User',
    'Zalo User',
    'Lazada User',
    'Telegram User',
    'WhatsApp User',
    'Whatsapp User',
    'TikTok User',
    'Zalo Personal User',
  ]);

  // ── Detection helpers ─────────────────────────────────────────────

  /** Kiểm tra tên khách có phải là tên mặc định (chưa sync) không */
  isDefaultCustomerName(name: string | null | undefined): boolean {
    if (!name) return false;
    return CustomerSyncService.DEFAULT_CUSTOMER_NAMES.has(name.trim());
  }

  /** Kiểm tra URL avatar có phải là avatar thật (không phải default placeholder) không */
  isRealAvatar(url: string | null | undefined): boolean {
    if (!url) return false;
    return !url.includes('default_avatar') && url.length > 0;
  }

  // ── Sync methods ─────────────────────────────────────────────────

  /**
   * Sync thông tin khách hàng (tên + avatar) cho 1 hội thoại.
   * Gọi API updateCustomer(syncName: 1) — BE fetch tên/avatar thật từ nền tảng.
   * Kết quả được cập nhật trực tiếp vào conversation list qua updateConversationInList.
   *
   * @returns { name, avatar } nếu có cập nhật, null nếu không cần sync hoặc lỗi
   */
  async syncCustomerData(
    conv: { id: string; fanpageUserId?: string; fanpageId?: string }
  ): Promise<{ name?: string; avatar?: string } | null> {
    if (!conv.fanpageUserId || !conv.fanpageId || this._syncing.has(conv.id)) return null;

    this._syncing.add(conv.id);
    try {
      const res = await firstValueFrom(this.api.updateCustomer({
        id: conv.id,
        syncName: 1,
        fanpageUserId: conv.fanpageUserId,
        fanpageId: conv.fanpageId,
      }));

      // Đọc dữ liệu từ response — thử nhiều common paths
      const data = (res as any)?.data;
      const newName: string | null = data?.name || data?.customer?.name || data?.fanpageUserName || null;
      const newAvatar: string | null = data?.customer?.avatar || data?.avatar || data?.customerAvatar || null;

      const updates: Partial<IConversation> = {};
      if (newName && typeof newName === 'string' && newName.trim()) {
        updates.fanpageUserName = newName.trim();
      }
      if (newAvatar && typeof newAvatar === 'string' && newAvatar.trim()) {
        updates.customerAvatar = newAvatar.trim();
      }

      if (Object.keys(updates).length > 0) {
        this.convListSvc.updateConversationInList(conv.id, updates);
      }

      return {
        name: updates.fanpageUserName,
        avatar: updates.customerAvatar,
      };
    } catch {
      return null;
    } finally {
      this._syncing.delete(conv.id);
    }
  }

  /**
   * Batch sync tên mặc định — gọi lần lượt bằng await.
   * Dùng sau khi load danh sách hội thoại lần đầu.
   */
  async syncDefaultNames(convs: IConversation[]): Promise<void> {
    for (const conv of convs) {
      if (!this.isDefaultCustomerName(conv.fanpageUserName)) continue;
      await this.syncCustomerData(conv);
    }
  }

  /**
   * Sync avatar cho 1 hội thoại bị lỗi (403/4xx).
   * Gọi khi image error event fired trên avatar trong sidebar.
   * Chỉ hỗ trợ kênh Facebook (1) và Zalo cá nhân (10) — BE các kênh khác chưa support.
   */
  async syncBrokenAvatar(
    conv: { id: string; channel?: number; fanpageUserId?: string; fanpageId?: string; customerAvatar?: string | null }
  ): Promise<void> {
    // Chỉ hỗ trợ Facebook, Zalo cá nhân, Tiktok Business
    if (![CHANNEL.FACEBOOK, CHANNEL.ZALO_PERSONAL, CHANNEL.TIKTOK_BUSINESS].includes(conv.channel || -1)) return;
    // Chỉ sync nếu avatar là URL thật (không phải default placeholder)
    if (!this.isRealAvatar(conv.customerAvatar)) return;
    await this.syncCustomerData(conv);
  }
}
